import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import EmbedContainer from 'react-oembed-container';
import { isNil } from 'lodash';
import {
  doExternalFetch,
  fetchOembed,
  getWindow,
  trackClickedSubscribeCTA,
  trackEvent,
} from '@surfline/web-common';
import theme from '@wavetrak/theme';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { AddToCalendar, CamPlayer, GoogleDFP, PremiumCamCTA } from '@surfline/quiver-react';
import ContentCarouselCard from 'components/ContentCarouselCard';
import ContentCarousel from 'components/ContentCarousel';
import PremiumVideoCTA from 'components/PremiumVideoCTA';
import ProductCard from 'components/ProductCard';
import loadAdConfig from './adConfig';
import { productCardTransformer, supplierCardTransformer } from './transformers';
import { fetchReport } from '../common/api/spot';
import { fetchCamEmbedDetails } from '../common/api/cameras';
import baseFetch, { doJSFetch } from '../common/baseFetch';
import config from '../config';

const win = getWindow();

const swellEventArticleButton =
  "<div class='sl-swell-event__article__image__button__wrapper'><div class='sl-swell-event__article__image__button'><svg xmlns='http://www.w3.org/2000/svg' class='jw-svg-icon jw-svg-icon-fullscreen-on' viewBox='0 0 240 240' focusable='false'><path d='M96.3,186.1c1.9,1.9,1.3,4-1.4,4.4l-50.6,8.4c-1.8,0.5-3.7-0.6-4.2-2.4c-0.2-0.6-0.2-1.2,0-1.7l8.4-50.6c0.4-2.7,2.4-3.4,4.4-1.4l14.5,14.5l28.2-28.2l14.3,14.3l-28.2,28.2L96.3,186.1z M195.8,39.1l-50.6,8.4c-2.7,0.4-3.4,2.4-1.4,4.4l14.5,14.5l-28.2,28.2l14.3,14.3l28.2-28.2l14.5,14.5c1.9,1.9,4,1.3,4.4-1.4l8.4-50.6c0.5-1.8-0.6-3.6-2.4-4.2C197,39,196.4,39,195.8,39.1L195.8,39.1z'></path></svg></div></div>";

export const getSkinPlayer = async (jwVideo, jwPlayer) => {
  if (!isNil(jwVideo) && !isNil(jwPlayer)) {
    try {
      const embedURL = `https://cdn.jwplayer.com/players/${jwVideo}-${jwPlayer}.js`;
      const data = await doJSFetch(embedURL);
      const result = data.substring(
        data.indexOf('var jwConfig = {'),
        data.indexOf('; // end config'),
      );
      const playerConfig = JSON.parse(result.replace('var jwConfig = ', ''));
      return playerConfig || {};
    } catch (error) {
      return {};
    }
  }
  return {};
};

export const formatProductLinks = () => {
  const productLinks = getWindow()?.document.querySelectorAll(`a[href^='${config.shopUrl}']`);
  if (productLinks.length) {
    [...productLinks].map(async (productLink) => {
      // set referrer-policy to ensure full URL is passed to shop for event tracking
      productLink.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
      // remove rel to ensure noreferrer is removed from link
      productLink.removeAttribute('rel');
    });
  }
};

export const renderIgElements = () => {
  const igElements = getWindow()?.document.querySelectorAll('[data-sl-ig]');
  if (igElements.length) {
    [...igElements].map(async (ig) => {
      const {
        oembedObj: { html },
      } = await fetchOembed(ig.dataset.slIg, '/api/content/oembed');
      ReactDOM.render(
        <EmbedContainer markup={html}>
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </EmbedContainer>,
        ig,
      );
    });
  }
};

export const renderJWElements = () => {
  let hasJwVideoStarted = false;
  const jwElements = getWindow()?.document.querySelectorAll("[id*='jw']");
  if (jwElements.length) {
    [...jwElements].map(async (jw) => {
      const [, jwId, jwPlayer] = jw.id.split('_');
      const playerConfig = await getSkinPlayer(jwId, jwPlayer);
      if (getWindow()?.jwplayer && jwId) {
        if (config.she.useSheMedia) {
          const blogherads = getWindow().blogherads || {};
          blogherads.adq = blogherads.adq || [];
          blogherads.adq.push(() => {
            blogherads.setConf('vertical', 'PreRoll_Article');
          });
        }
        let manuallyStartVideoTimeout;
        let player = getWindow()
          .jwplayer(jw.id)
          .setup({ ...playerConfig });
        const manuallyStartVideo = () => {
          if (
            hasJwVideoStarted === false &&
            playerConfig.advertising &&
            player.getState() !== 'playing'
          ) {
            delete playerConfig.advertising;
            player = getWindow()
              .jwplayer(jw.id)
              .setup({ ...playerConfig });
            player.play();
            hasJwVideoStarted = true;
          }
        };
        player
          .on('beforePlay', () => {
            manuallyStartVideoTimeout = setTimeout(manuallyStartVideo, 3000);
          })
          .on('adBreakStart', () => {
            hasJwVideoStarted = true;
          })
          .on('firstFrame', () => {
            hasJwVideoStarted = true;
          })
          .on('remove', () => {
            clearTimeout(manuallyStartVideoTimeout);
          });
      }
    });
  }
};

export const unmountJWElements = () => {
  const jwElements = getWindow()?.document.querySelectorAll('.sl-jw-video-wrap');
  if (jwElements.length) {
    jwElements.forEach((_player, index) => {
      getWindow().jwplayer(index).remove();
    });
  }
};

export const renderTwitterElements = () => {
  const twitterElements = win?.document.getElementsByClassName('twitter-tweet');
  if (twitterElements.length) {
    [...twitterElements].map(async (tweetElement) => {
      const tweetUrl = tweetElement.getElementsByTagName('a')[1].href;
      const {
        oembedObj: { html },
      } = await fetchOembed(tweetUrl, '/api/content/oembed');
      ReactDOM.render(
        <EmbedContainer markup={html}>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </EmbedContainer>,
        tweetElement,
      );
    });
  }
};

const createCameraObject = (cameraDetails) => ({
  streamUrl: cameraDetails.cam.streamUrl,
  stillUrl: cameraDetails.cam.stillUrl,
  status: {
    isDown: !!cameraDetails.cam.isDown.status,
    message: cameraDetails.cam.isDown.message,
    subMessage: cameraDetails.cam.isDown.subMessage,
  },
  isPrerecorded: false,
  isPremium: cameraDetails.cam.isPremium,
  title: 'title',
});

/**
 * @param {boolean} isPremium
 */
export const renderCameraEmbeds = (isPremium) => {
  const cameraEmbeds = win?.document.getElementsByClassName('wp-cam-embed');
  if (cameraEmbeds.length) {
    [...cameraEmbeds].map(async (embedElement) => {
      const cameraId = embedElement.getAttribute('data-sl-cam-id');
      const detailsJson = await fetchCamEmbedDetails(cameraId);
      const spotReport = await fetchReport(detailsJson.spot.id);
      const camera = createCameraObject(detailsJson);

      const camStillUrl = camera.stillUrl;
      const isPremiumCam = camera.isPremium;
      const showPremiumCamPaywall = isPremiumCam && !isPremium;
      const segmentProperties = {
        location: 'premium only cam - cam embed',
        title: 'Premium Analysis',
        category: 'forecast',
        spotId: detailsJson.spot.id,
        spotName: detailsJson.spot.name,
      };
      if (showPremiumCamPaywall) {
        ReactDOM.render(
          <div className="video-wrap-container">
            <div className="video-wrap">
              <PremiumCamCTA
                camStillUrl={camStillUrl}
                href={`${config.surflineHost}${config.funnelUrl}`}
                onClickPaywall={() => trackClickedSubscribeCTA(segmentProperties)}
              />
            </div>
          </div>,
          embedElement,
        );
      } else {
        ReactDOM.render(
          <div className="video-wrap-container">
            <div className="video-wrap">
              {/* TODO: Replace with CamPlayerV2 with final tear down */}
              <CamPlayer
                aspectRatio="16:9"
                adTag={{ iu: '/1024858/SL_Editorial_Embed_PreRoll' }}
                advertisingIds={spotReport.associated.advertising}
                playerId={cameraId || 'sl-editorial-embed'}
                camera={camera}
                spotName={detailsJson.spot.name}
                spotId={detailsJson.spot.id}
                isPremium={isPremium}
                isMultiCam={false}
                location="Editorial Embed"
              />
            </div>
          </div>,
          embedElement,
        );
      }
    });
  }
};

export const renderYoutubeEmbeds = () => {
  const youtubeEmbedContainers = win?.document?.querySelectorAll(
    '.sl-swell-event__article-container .quiver-forecast-article-card__body > p',
  );

  if (youtubeEmbedContainers.length) {
    youtubeEmbedContainers.forEach((container) => {
      const youtubeIframe = container.querySelector('iframe[src^="https://www.youtube.com/embed/');

      if (youtubeIframe) {
        ReactDOM.render(
          <div itemScope itemType="https://schema.org/VideoObject">
            <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '56.25%' }}>
              <iframe
                src={youtubeIframe.src}
                width="100%"
                height="100%"
                title="Youtube Video"
                style={{ position: 'absolute' }}
                allowFullScreen
              />
            </div>
          </div>,
          container,
        );
      }
    });
  }
};

export const replaceWithClickableImage = (onClick, getImages) => {
  const imgElements = win?.document.querySelectorAll(
    '.sl-swell-event__content__wrapper p > img, .wp-caption > img',
  );
  const images = [];
  if (imgElements.length) {
    [...imgElements].map((img, index) => {
      /* eslint-disable-next-line no-param-reassign */
      img.onclick = () => onClick(index);
      img.insertAdjacentHTML('afterend', swellEventArticleButton);
      images.push({ src: img.src, w: img.clientWidth, h: img.clientHeight, index });
      return img;
    });
  }
  getImages(images);
  return null;
};

export const renderFeaturedVideo = (jwId) => {
  const featuredVideoElements = win?.document.getElementsByClassName(
    'sl-swell-event-featured-media',
  );
  if (featuredVideoElements.length) {
    [...featuredVideoElements].map(async (jw) => {
      if (win && win.jwplayer && jwId) {
        win
          .jwplayer(jw.id)
          .setup({ playlist: `https://content.jwplatform.com/feeds/${jwId}.json`, ph: 2 });
      }
    });
  }
};

export const renderResizedImages = () => {
  const imgElements = win?.document.getElementsByTagName('img');
  if (imgElements.length) {
    [...imgElements].map((img) => {
      if (img.src.indexOf('/wp-content/uploads') > -1) {
        /* eslint-disable-next-line no-param-reassign */
        img.src = config.cloudflareImageResizingUrl(img.src);
        return img;
      }
      return img;
    });
  }
};

const renderAd = (element, adNumber, adIdentifier, dfpKeyword, qaFlag) => {
  element.insertAdjacentHTML('afterend', `<div id="ad-${adNumber}" />`);
  ReactDOM.render(
    <GoogleDFP
      adConfig={loadAdConfig(adIdentifier, [
        ['qaFlag', qaFlag],
        ['contentKeyword', dfpKeyword],
      ])}
      isHtl
      isTesting={config.htl.isTesting}
      useSheMedia={config.she.useSheMedia}
    />,
    getWindow()?.document.getElementById(`ad-${adNumber}`),
  );
};

export const renderSurfNetworkVideo = async () => {
  const surfNetworkContainerElement = getWindow()?.document.getElementById('sl-surfnetwork-video');
  if (surfNetworkContainerElement) {
    const videoId = surfNetworkContainerElement.getAttribute('data-sl-video-id');
    if (videoId) {
      const surfNetworkToken = await doExternalFetch('/api/content/surfnetwork');
      ReactDOM.render(
        <div itemScope itemType="https://schema.org/VideoObject">
          <div style={{ position: 'relative', overflow: 'hidden', paddingBottom: '56.25%' }}>
            <iframe
              src={`https://api.nodplatform.com/partner/surfline/watch/${videoId}.html?access_token=${surfNetworkToken}`}
              width="100%"
              height="100%"
              title="SurfNetwork"
              style={{ position: 'absolute' }}
              allowFullScreen
            />
          </div>
        </div>,
        surfNetworkContainerElement,
      );
    }
  }
};

export const renderArticleAds = (articleRef, children, dfpKeyword, createdAt, qaFlag) => {
  const adTracker = { count: 0, placed: 0 };
  children.forEach((node, index) => {
    if (
      node.tagName === 'P' ||
      node.tagName === 'IMG' ||
      node.className.indexOf('video') > -1 ||
      node.className.indexOf('img') > -1
    ) {
      adTracker.count += 1;
    }
    if (adTracker.count === 4 && adTracker.placed < 3) {
      const adNumber = adTracker.placed + 1;
      const adIdentifier =
        adNumber === 1 && new Date(createdAt) < new Date('2019-04-29 00:00:00')
          ? 'legacyEditContent'
          : `editBody${adNumber}`;
      renderAd(articleRef.current.children[index], adNumber, adIdentifier, dfpKeyword, qaFlag);
      adTracker.count = -1;
      adTracker.placed += 1;
    } else if (index === children.length - 1 && adTracker.placed === 0) {
      const adNumber = adTracker.placed + 1;
      renderAd(
        articleRef.current.children[index],
        adNumber,
        `editBody${adNumber}`,
        dfpKeyword,
        qaFlag,
      );
    }
  });
};

export const trackArticleLinkClicks = (articleId) => {
  const articleBody = getWindow()?.document.getElementById('sl-editorial-article-body');
  const articleLinks = articleBody.querySelectorAll('a');
  if (articleLinks.length) {
    [...articleLinks].map(async (ig, index) => {
      articleLinks[index].addEventListener(
        'click',
        (event) =>
          trackEvent('Clicked Link', {
            articleId,
            category: 'editorial',
            linkLocation: 'editorialContent',
            linkName: event.target.innerText || articleLinks[index].innerText,
            linkUrl: event.target.href || articleLinks[index].href,
          }),
        false,
      );
    });
  }
};

export const renderVideoPaywalls = (onClickVideoPaywall, onClickSignIn, shouldPaywall) => {
  const videoPaywallElement = getWindow()?.document.getElementById('sl-premium-video-cta');
  const videoPaywallCaptionElements =
    getWindow()?.document.getElementsByClassName('sl-premium-video-text');

  if (videoPaywallElement && shouldPaywall) {
    ReactDOM.render(
      <PremiumVideoCTA
        onClickVideoPaywall={onClickVideoPaywall}
        imageUrl={videoPaywallElement.getAttribute('data-image-url')}
        onClickSignIn={onClickSignIn}
        videoText={
          videoPaywallCaptionElements.length ? videoPaywallCaptionElements[0].innerHTML : ''
        }
      />,
      videoPaywallElement,
    );
  }
};

const onClickAddToCalendar = (trackData) => {
  trackEvent('Clicked Link', trackData);
};

export const renderCalendarEvents = (articleId) => {
  const eventElements = getWindow()?.document.getElementsByClassName('cal-event');
  if (eventElements.length) {
    [...eventElements].map(async (event) => {
      try {
        const spotReport = await fetchReport(event.querySelector('#cal-event-spot').innerHTML);
        const {
          spot: { name: spotName },
          associated: { href: spotUrl },
        } = spotReport;
        const startTime = event.querySelector('#cal-event-start').innerHTML.toString();
        const endTime = event.querySelector('#cal-event-end').innerHTML.toString();
        const eventObj = {
          title: `Surf ${spotName}`,
          description: `Check out the forecast for ${spotName} at ${spotUrl}`,
          location: spotName,
          startTime,
          endTime,
        };
        const trackData = {
          articleId,
          category: 'editorial',
          linkLocation: 'editorialContent',
          linkName: 'Add to Calendar',
          linkUrl: spotUrl,
        };
        ReactDOM.render(
          <AddToCalendar onClickAdd={() => onClickAddToCalendar(trackData)} event={eventObj} />,
          event,
        );
      } catch (err) {
        console.error(err);
      }
    });
  }
};

export const renderCalendarCinemaEvents = (articleId) => {
  const eventElements = getWindow()?.document.getElementsByClassName('cal-event-cinema');
  if (eventElements.length) {
    [...eventElements].map(async (event) => {
      try {
        const startTime = event.querySelector('#cal-event-start').innerHTML.toString();
        const endTime = event.querySelector('#cal-event-end').innerHTML.toString();
        const description = event.querySelector('#cal-event-description').innerHTML.toString();
        const title = event.querySelector('#cal-event-title').innerHTML.toString();
        const link = event.querySelector('#cal-event-link').innerHTML.toString();
        const eventObj = {
          title,
          description: `${description}
          ${link}`,
          startTime,
          endTime,
        };
        const trackData = {
          articleId,
          category: 'editorial',
          linkLocation: 'editorialContent',
          linkName: 'Add to Calendar',
          linkUrl: link,
        };
        ReactDOM.render(
          <AddToCalendar onClickAdd={() => onClickAddToCalendar(trackData)} event={eventObj} />,
          event,
        );
      } catch (err) {
        console.error(err);
      }
    });
  }
};

const ProductCarousel = ({
  endpoint,
  title,
  subtitle,
  carouselItem,
  backgroundColor,
  ctaCopy,
  ctaUrl,
}) => {
  const DEFAULT_TITLE = 'Shop';
  const DEFAULT_SUBTITLE = 'Surfline premium members save when they shop';
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="sl-article-body-content-carousel">
          <ContentCarousel
            callToActionButton={{
              isDark: false,
              isExternal: true,
              isReferrer: true,
              text: ctaCopy || 'View All',
              link: ctaUrl || config.shopUrl,
              startIcon: true,
            }}
            carouselItem={carouselItem}
            colors={{
              bg: backgroundColor || theme.expressive.marineLayer,
              skeletonBg: theme.palette.text.light,
              skeletonItem: theme.palette.text.extraLight,
              headerText: theme.palette.text.brandSecondary,
              headerButton: theme.palette.text.brandSecondary,
              noItemsText: theme.palette.text.base,
            }}
            dataTransform={(data) => data?.data?.products}
            fetchConfig={{ endpoint, fetch: baseFetch }}
            isProductCarousel
            itemAspectRatio={1.32}
            noItemsMessage={{
              message:
                'Find surf essentials in the Surfline shop, with special offers for Premium members.',
              ctaCopy: 'Check it out',
              ctaUrl: config.shopUrl,
            }}
            numberOfitems={4}
            openCtaInNewTab
            screenConfig={{ small: 3, mobile: 2, medium: 4, large: 4 }}
            showLogo
            showPageCount={false}
            style={{ marginBottom: 0 }}
            subtitle={subtitle || DEFAULT_SUBTITLE}
            title={title || DEFAULT_TITLE}
          />
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export const renderProductCarousel = (segmentProperties) => {
  const productElements = getWindow()?.document.querySelectorAll(
    'div[data-sl-product-carousel-product-ids]',
  );

  if (productElements?.length) {
    try {
      [...productElements].map(async (productElement) => {
        const productIds = productElement.attributes['data-sl-product-carousel-product-ids']?.value;
        const carouselTitle = productElement.attributes['data-sl-product-carousel-title']?.value;
        const carouselSubtitle =
          productElement.attributes['data-sl-product-carousel-subtitle']?.value;
        const ctaCopy = productElement.attributes['data-sl-product-carousel-cta-copy']?.value;
        const ctaUrl = productElement.attributes['data-sl-product-carousel-cta-url']?.value;
        const ctaSuffix = productElement.attributes['data-sl-product-carousel-cta-suffix']?.value;
        const backgroundColor =
          productElement.attributes['data-sl-product-carousel-bg-color']?.value;
        const endpoint = `/store/products?productIds=${productIds}`;

        const carouselItem = (itemData, index) => (
          <ProductCard
            data={productCardTransformer(itemData, ctaSuffix)}
            trackCardClick={() => {
              trackEvent('Carousel Clicked', {
                ...segmentProperties,
                location: 'shop module',
                positionNumber: index,
                productId: itemData?.id,
                productName: itemData?.name,
                brandName: itemData?.brand?.name,
              });
            }}
          />
        );

        ReactDOM.render(
          <ProductCarousel
            title={carouselTitle}
            subtitle={carouselSubtitle}
            carouselItem={carouselItem}
            endpoint={endpoint}
            ctaCopy={ctaCopy}
            ctaUrl={ctaUrl}
            backgroundColor={backgroundColor}
          />,
          productElement,
        );
      });
    } catch (err) {
      console.log('An error was recieved while replacing the article Product Carousel: ', err);
    }
  }
};

const TravelCardCarousel = ({ endpoint, subtitle, title, carouselItem }) => {
  const DEFAULT_TITLE = 'Travel';
  const DEFAULT_SUBTITLE = 'Thinking about a surf trip? You’ve got options.';
  const [supplierCount, setSupplierCount] = useState(0);
  const cardsToDisplay = useMemo(
    () => (supplierCount > 1 && supplierCount < 4 ? supplierCount : 3),
    [supplierCount],
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="sl-article-body-content-carousel">
          <ContentCarousel
            carouselItem={carouselItem}
            colors={{
              bg: '#3B8E9F',
              skeletonBg: '#206977',
              skeletonItem: '#1d7b8e',
              headerText: 'white',
              headerButton: 'white',
            }}
            dataTransform={(data) => {
              setSupplierCount(data?.suppliers.length);
              return data?.suppliers;
            }}
            fetchConfig={{ endpoint, fetch: baseFetch }}
            itemAspectRatio={1.1}
            numberOfitems={cardsToDisplay}
            screenConfig={{ small: 2, mobile: 1, medium: cardsToDisplay }}
            showLogo
            showPageCount
            style={{ marginBottom: 0 }}
            subtitle={subtitle || DEFAULT_SUBTITLE}
            title={title || DEFAULT_TITLE}
          />
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

/**
 * @param segmentProperties
 * brand: 'sl',
 * location: '',
 * path: '',
 * positionNumber: '',
 * postCategories: '',
 * postId: article.id,
 * category: 'travel',
 * provider: 'Booking Layer',
 * referrer: '',
 * search: '',
 * travelSupplierId: '',
 * travelSupplierName: '',
 * travelZone: '',
 * title: displayTitle || title,
 * url: permalink,
 * */
export const renderTravelCardCarousel = (segmentProperties) => {
  const travelElements = getWindow()?.document.querySelectorAll(
    'div[data-sl-travel-carousel-supplier-ids]',
  );

  if (travelElements.length) {
    try {
      [...travelElements].map(async (travelElement) => {
        const queryString = travelElement.attributes['data-sl-travel-carousel-supplier-ids']?.value
          ?.split(',')
          .map((supplier) => supplier.split('|'))
          .map((paramArray, index) => {
            const prefix = `suppliers[${index}]`;

            const parameter = paramArray
              .map((param) => {
                if (param.includes(':')) {
                  const split = [...param.split(':')];
                  return `${prefix}[${split[0]}]=${split[1]}`;
                }
                return `${prefix}[id]=${param}`;
              })
              .join('&');

            return parameter;
          })
          .join('&');

        const carouselTitle = travelElement.attributes['data-sl-travel-carousel-title']?.value;
        const carouselSubtitle =
          travelElement.attributes['data-sl-travel-carousel-subtitle']?.value;

        const endpoint = `/travel/suppliers?${queryString}`;

        const carouselItem = (itemData, index) => (
          <ContentCarouselCard
            data={supplierCardTransformer({
              ...itemData,
              link: `/travel/surf-camp/${itemData?.slug}`,
            })}
            trackCardClick={() => {
              trackEvent('Carousel Clicked', {
                ...segmentProperties,
                location: 'travel module',
                positionNumber: index,
                referrer: '',
                search: '',
                travelSupplierId: itemData?.id,
                travelSupplierName: itemData?.name,
                travelZone: itemData?.travel_zones[0],
              });
            }}
          />
        );

        ReactDOM.render(
          <TravelCardCarousel
            title={carouselTitle}
            subtitle={carouselSubtitle}
            carouselItem={carouselItem}
            endpoint={endpoint}
          />,
          travelElement,
        );
      });
    } catch (err) {
      console.log('An error was recieved while replacing the article Travel Module: ', err);
    }
  }
};
